import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button
} from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import Layout from './layout';
import { useOtpSettings } from './OtpContext';
import { clearOtpCache } from '../utility/otpHelper';
import { clearComponentSettingsCache } from '../utility/componentSettingsHelper';

const OTP_SETTING = 'OTP Verification';

const ManageSettings = () => {
  const { reloadSettings } = useOtpSettings();
  const [institutes, setInstitutes] = useState([]);
  const [selectedInstituteId, setSelectedInstituteId] = useState(null);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const fetchInstitutes = async () => {
    try {
      const response = await axios.get('/institute/getInstitutes', { withCredentials: true });
      const instituteList = Array.isArray(response.data)
        ? response.data
        : response.data?.institutes || [];
      setInstitutes(instituteList);
      const defaultInstituteId = Number(localStorage.getItem('instituteId')) || (instituteList[0] && instituteList[0].institute_id);
      setSelectedInstituteId(defaultInstituteId);
      return instituteList;
    } catch (err) {
      console.error('Failed to load institutes:', err);
      setSnackbarSeverity('error');
      setSnackbarMessage('Could not load institutes.');
      setSnackbarOpen(true);
      return [];
    }
  };

  const fetchSettings = async (instituteId) => {
    if (!instituteId) {
      setComponents([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const [otpResponse, downloadReportResponse] = await Promise.all([
        axios.get('/api/otp-settings', {
          params: { institute_id: instituteId },
          withCredentials: true
        }),
        axios.get('/api/component-settings', {
          params: { institute_id: instituteId, setting_name: 'Download Report' },
          withCredentials: true
        })
      ]);

      const otpData = Array.isArray(otpResponse.data)
        ? otpResponse.data
        : otpResponse.data?.settings || [];

      const downloadReportSettings = (downloadReportResponse.data || []).map((item) => ({
        component_id: item.component_id,
        component_name: item.component_name,
        setting_name: 'Download Report',
        is_enabled: item.is_enabled,
        description: item.description || 'No description available'
      }));

      // Components configured for another setting (e.g. StudentManagement, which only
      // has "Download Report") aren't real OTP components — the OTP list endpoint
      // defaults every component_master row to enabled, so exclude those here.
      const nonOtpComponentIds = new Set(downloadReportSettings.map((item) => String(item.component_id)));
      const otpSettings = (otpData || [])
        .filter((item) => !nonOtpComponentIds.has(String(item.component_id)))
        .map((item) => ({
          component_id: item.component_id,
          component_name: item.component_name,
          setting_name: OTP_SETTING,
          is_enabled: item.is_otp_enabled,
          description: item.description || 'No description available'
        }));

      setComponents([...otpSettings, ...downloadReportSettings]);
    } catch (err) {
      console.error('Failed to load settings:', err);
      setSnackbarSeverity('error');
      setSnackbarMessage('Could not load settings for the selected institute.');
      setSnackbarOpen(true);
      setComponents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const instituteList = await fetchInstitutes();
      const targetInstituteId = Number(localStorage.getItem('instituteId')) || (instituteList[0] && instituteList[0].institute_id);
      if (targetInstituteId) {
        setSelectedInstituteId(targetInstituteId);
        await fetchSettings(targetInstituteId);
      } else {
        setLoading(false);
      }
    };
    initialize();
  }, []);

  const handleInstituteChange = (event) => {
    const instituteId = event.target.value;
    setSelectedInstituteId(instituteId);
  };

  const handleGoClick = async () => {
    if (selectedInstituteId) {
      localStorage.setItem('instituteId', selectedInstituteId);
      // Dispatch custom event to notify OTP context / component-setting consumers of institute change
      window.dispatchEvent(new Event('instituteChanged'));
      await fetchSettings(selectedInstituteId);
      await reloadSettings();
    }
  };

  const handleToggle = async (component) => {
    try {
      setUpdating(true);
      setSnackbarOpen(false);

      if (component.setting_name === OTP_SETTING) {
        await axios.patch('/api/otp-settings', {
          institute_id: selectedInstituteId,
          component_id: component.component_id,
          is_otp_enabled: !component.is_enabled
        }, { withCredentials: true });
        clearOtpCache();
      } else {
        await axios.patch('/api/component-settings', {
          institute_id: selectedInstituteId,
          component_id: component.component_id,
          setting_name: component.setting_name,
          is_enabled: !component.is_enabled
        }, { withCredentials: true });
        clearComponentSettingsCache(component.component_name, component.setting_name);
      }

      setComponents((prev) => prev.map((item) =>
        item.component_id === component.component_id && item.setting_name === component.setting_name
          ? { ...item, is_enabled: !item.is_enabled }
          : item
      ));
      window.dispatchEvent(new Event('instituteChanged'));
      await reloadSettings();
      setSnackbarSeverity('success');
      setSnackbarMessage(`${component.setting_name} for ${component.component_name} has been ${!component.is_enabled ? 'enabled' : 'disabled'}.`);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error updating setting:', err);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to update setting. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setUpdating(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Container>
        <Layout />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ marginTop: '2rem', marginBottom: '2rem' }}>
      <Layout />
      <Box sx={{ marginTop: '2rem' }}>
        <h1 className="title">
          🔒 Manage Settings - Access Control Panel
        </h1>
        <Typography variant="body1" sx={{ marginBottom: '1.5rem', color: 'text.secondary' }}>
             Select Institute to control settings for listed modules. Toggle to enable/disable.
        </Typography>

        <Box sx={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '0.5rem' }}>
          <FormControl sx={{ minWidth: 350, maxHeight: 60 }}>
            <InputLabel id="institute-select-label">Select Institute</InputLabel>
            <Select
              labelId="institute-select-label"
              value={selectedInstituteId || ''}
              label="Select Institute"
              onChange={handleInstituteChange}
              size="small"
            >
              {institutes.map((institute) => (
                <MenuItem key={institute.institute_id} value={institute.institute_id}>
                  {institute.institute_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleGoClick}
            disabled={!selectedInstituteId}
            sx={{ backgroundColor: '#0A64A2', '&:hover': { backgroundColor: '#084a7a' } }}
          >
            Go
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#0A64A2' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Module</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Setting</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Description</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#fff' }}>Enabled</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#fff' }}>Toggle</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {components.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ padding: '2rem' }}>
                    No settings found for this institute.
                  </TableCell>
                </TableRow>
              ) : (
                components.map((component) => (
                  <TableRow key={`${component.component_id}-${component.setting_name}`} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f7f9fc' } }}>
                    <TableCell sx={{ fontWeight: 500 }}>{component.component_name}</TableCell>
                    <TableCell>{component.setting_name}</TableCell>
                    <TableCell>{component.description}</TableCell>
                    <TableCell align="center">
                      {component.is_enabled ? (
                        <CheckCircle sx={{ color: '#4caf50', fontSize: '1.5rem' }} />
                      ) : (
                        <Cancel sx={{ color: '#f44336', fontSize: '1.5rem' }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={component.is_enabled}
                        onChange={() => handleToggle(component)}
                        disabled={updating}
                        color="primary"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ManageSettings;
